import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Users, TrendingUp, Heart, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const ProgramDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.programDetail(slug));
        setProgram(response.data);
      } catch (err) {
        console.error('Failed to fetch program:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-navy border-t-transparent mx-auto" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-navy mb-4">Programme non trouve</h2>
          <button onClick={() => navigate('/programmes')} className="px-6 py-3 rounded-full bg-navy text-white font-sans font-semibold">Retour</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>{`${program.title} - HPD Programmes`}</title>
        <meta name="description" content={program.short_description || ''} />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] overflow-hidden">
        <img src={program.images[0]} alt={program.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-navy/60" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={() => navigate('/programmes')} className="text-white/80 hover:text-white mb-6 flex items-center text-sm font-sans">
                <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux programmes
              </button>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight max-w-3xl">{program.title}</h1>
              <p className="text-lg text-white/80 max-w-2xl font-sans">{program.short_description}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-forest py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {Object.entries(program.impact_stats).map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="text-3xl md:text-4xl font-serif font-bold text-white mb-1">{value}</div>
                <div className="text-sm text-white/80 capitalize font-sans">{key.replace(/_/g, ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">
              <div>
                <h2 className="text-3xl font-serif font-bold text-navy mb-6">A Propos du Programme</h2>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed font-sans">{program.full_description}</p>
              </div>

              <div>
                <h2 className="text-3xl font-serif font-bold text-navy mb-6">Nos Objectifs</h2>
                <div className="space-y-4">
                  {program.objectives.map((obj, i) => (
                    <div key={`obj-${i}`} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-forest flex-shrink-0 mt-1" />
                      <p className="text-base text-gray-700 font-sans">{obj}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-warmgray rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="w-7 h-7 text-navy" />
                  <h3 className="text-2xl font-serif font-bold text-navy">Beneficiaires</h3>
                </div>
                <p className="text-base text-gray-700 font-sans">{program.beneficiaries}</p>
              </div>

              {program.success_stories && program.success_stories.length > 0 && (
                <div>
                  <h2 className="text-3xl font-serif font-bold text-navy mb-6">Histoires de Succes</h2>
                  <div className="space-y-4">
                    {program.success_stories.map((story, i) => (
                      <div key={story.name || `story-${i}`} className="bg-white rounded-2xl p-8 border-l-4 border-forest shadow-sm">
                        <p className="text-base text-gray-700 italic mb-4 font-serif">"{story.story}"</p>
                        <p className="font-semibold text-navy font-sans text-sm">-- {story.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {program.images && program.images.length > 1 && (
                <div>
                  <h2 className="text-3xl font-serif font-bold text-navy mb-6">Galerie</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {program.images.slice(1).map((image, i) => (
                      <div key={`img-${image}`} className="rounded-2xl overflow-hidden group">
                        <img src={image} alt={`${program.title} ${i + 1}`} className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="bg-navy rounded-3xl p-8 text-white">
                  <Heart className="w-10 h-10 mb-4 text-forest" />
                  <h3 className="text-2xl font-serif font-bold mb-4">Comment Soutenir</h3>
                  <p className="text-white/70 text-sm font-sans mb-6 leading-relaxed">{program.how_to_support}</p>
                  <button onClick={() => navigate('/don')} className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full bg-forest text-white font-semibold font-sans hover:bg-forest/90 transition-all">
                    Faire un Don <ArrowRight className="ml-2 w-4 h-4" />
                  </button>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm">
                  <h3 className="text-lg font-serif font-bold text-navy mb-4">Autres Programmes</h3>
                  <Link to="/programmes" className="text-forest hover:text-forest/80 text-sm font-sans font-semibold">Voir tous les programmes &rarr;</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-warmgray">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TrendingUp className="w-12 h-12 text-forest mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6 tracking-tight">Rejoignez Notre Mission</h2>
          <p className="text-base md:text-lg text-gray-600 mb-10 font-sans">Chaque contribution fait une difference dans la vie de nos beneficiaires</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/don')} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-navy text-white font-semibold hover:bg-navy/90 transition-all font-sans">Faire un Don</button>
            <button onClick={() => navigate('/interventions')} className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-forest text-forest font-semibold hover:bg-forest hover:text-white transition-all font-sans">Decouvrir Nos Axes</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
